import {
  Form,
  InputNumber,
  Modal,
  Select,
  Switch,
  message,
} from 'antd';
import { useBackdropClose } from '../hooks/useBackdropClose';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api';
import { LOCATION_CHECK_TARIFF_NAME } from '../constants/locationCheck';
import LocationCheckChart from '../components/location-check-chart/LocationCheckChart';
import { DashboardSalesDateRange } from '../components/dashboard-sales-panel';
import {
  addMskCalendarDays,
  createMskCalendarDate,
  formatMskDateRangeLabel,
  formatMskShortDate,
  getMskRangeSpanDays,
  getMskToday,
  normalizeMskCalendarDate,
} from '../utils/moscow-calendar-date';
import '../components/dashboard-sales-panel/dashboard-sales-panel.css';
import '../styles/location-check.css';

const BAND_OPTIONS = [
  { value: 'B1', label: 'B1' },
  { value: 'B3', label: 'B3' },
  { value: 'B7', label: 'B7' },
  { value: 'B8', label: 'B8' },
  { value: 'B20', label: 'B20' },
];

const RANGE_OPTIONS = [
  { key: '24h', label: 'День' },
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
];

const CHART_TYPE_OPTIONS = [
  { key: 'line', label: 'Линия' },
  { key: 'bar', label: 'Бар' },
];

const AGGREGATION_OPTIONS = [
  { key: 'hourly', label: 'По часам' },
  { key: 'daily', label: 'По дням' },
];

const SPEED_METRIC_OPTIONS = [
  { key: 'download', label: 'Download' },
  { key: 'upload', label: 'Upload' },
];

const RF_METRIC_OPTIONS = [
  { key: 'rsrp', label: 'RSRP' },
  { key: 'snr', label: 'SNR' },
];

const formatMskDayIso = (date, endOfDay = false) => {
  const normalized = normalizeMskCalendarDate(date);
  if (!normalized) return null;
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, '0');
  const day = String(normalized.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T${endOfDay ? '23:59:59' : '00:00:00'}+03:00`;
};

/** Текущий момент wall-clock МСК с offset +03:00. */
const formatMskNowIso = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}+03:00`;
};

/** Пн…min(Вс, сегодня) недели, содержащей anchor (MSK calendar). */
const getMskWeekRange = (anchorDate = getMskToday()) => {
  const today = getMskToday();
  const day = normalizeMskCalendarDate(anchorDate) || today;
  const jsDay = day.getDay();
  const mondayOffset = jsDay === 0 ? -6 : 1 - jsDay;
  const rangeStart = addMskCalendarDays(day, mondayOffset);
  const weekEnd = addMskCalendarDays(rangeStart, 6);
  const rangeEnd = weekEnd.getTime() > today.getTime() ? today : weekEnd;
  return { rangeStart, rangeEnd };
};

/** 1…min(последний день, сегодня) календарного месяца (MSK). */
const getMskMonthRange = (anchorDate = getMskToday()) => {
  const today = getMskToday();
  const day = normalizeMskCalendarDate(anchorDate) || today;
  const year = day.getFullYear();
  const month = day.getMonth() + 1;
  const rangeStart = createMskCalendarDate(year, month, 1);
  const lastDay = new Date(year, month, 0).getDate();
  const monthEnd = createMskCalendarDate(year, month, lastDay);
  const rangeEnd = monthEnd.getTime() > today.getTime() ? today : monthEnd;
  return { rangeStart, rangeEnd };
};

const buildPeriodRange = (periodKey, anchorDate = getMskToday()) => {
  const today = getMskToday();
  if (periodKey === 'week') {
    return getMskWeekRange(anchorDate);
  }
  if (periodKey === 'month') {
    return getMskMonthRange(anchorDate);
  }
  // 24ч — календарный день якоря (сегодня = rolling при запросе)
  const day = normalizeMskCalendarDate(anchorDate) || today;
  return { rangeStart: day, rangeEnd: day };
};

const toSeriesBounds = (_periodKey, rangeStart, rangeEnd) => {
  const today = getMskToday();
  const start = normalizeMskCalendarDate(rangeStart);
  const end = normalizeMskCalendarDate(rangeEnd);
  // Один день = календарные сутки МСК (00:00→сейчас / 00:00→23:59), НЕ rolling 24ч
  if (start && end && start.getTime() === end.getTime()) {
    const isToday = start.getTime() === today.getTime();
    return {
      from_ts: formatMskDayIso(start, false),
      to_ts: isToday ? formatMskNowIso() : formatMskDayIso(start, true),
    };
  }
  return {
    from_ts: formatMskDayIso(start || today, false),
    to_ts: formatMskDayIso(end || today, true),
  };
};

const SettingsGearIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.36.51.97.84 1.64.84H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TARGET_STATUS_LABELS = {
  idle: 'ожидание',
  queued: 'в очереди',
  running: 'выполняется',
  paused: 'пауза',
  error: 'ошибка',
};

const statusBadgeClass = (status) => {
  const key = String(status || 'idle').toLowerCase();
  if (key === 'running') return 'lc-badge lc-badge--running';
  if (key === 'paused') return 'lc-badge lc-badge--paused';
  if (key === 'error') return 'lc-badge lc-badge--error';
  if (key === 'queued') return 'lc-badge lc-badge--queued';
  return 'lc-badge lc-badge--idle';
};

const statusBadgeLabel = (status) => {
  const key = String(status || 'idle').toLowerCase();
  return TARGET_STATUS_LABELS[key] || key;
};

const TargetCard = ({
  target,
  actionId,
  onPause,
  onResume,
  onRunNow,
  onRemove,
}) => {
  const [chartType, setChartType] = useState('line');
  const [aggregation, setAggregation] = useState('hourly');
  const [metric, setMetric] = useState('download');
  const [rfMetric, setRfMetric] = useState('rsrp');
  const initialPeriod = buildPeriodRange('24h');
  const [periodFilter, setPeriodFilter] = useState('24h');
  const [rangeStart, setRangeStart] = useState(initialPeriod.rangeStart);
  const [rangeEnd, setRangeEnd] = useState(initialPeriod.rangeEnd);
  const [seriesPayload, setSeriesPayload] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [hoverState, setHoverState] = useState(null);
  const hoverLeaveTimerRef = useRef(null);

  const dateRangeLabel = useMemo(
    () => `${formatMskDateRangeLabel(rangeStart, rangeEnd)} МСК`,
    [rangeStart, rangeEnd],
  );

  const isSingleDayRange = useMemo(() => {
    const start = normalizeMskCalendarDate(rangeStart);
    const end = normalizeMskCalendarDate(rangeEnd);
    return Boolean(start && end && start.getTime() === end.getTime());
  }, [rangeStart, rangeEnd]);

  const tooltipDayLabel = useMemo(
    () => (isSingleDayRange ? formatMskShortDate(rangeStart) : null),
    [isSingleDayRange, rangeStart],
  );

  const canGoNext = useMemo(() => {
    const end = normalizeMskCalendarDate(rangeEnd);
    const today = getMskToday();
    if (!end) return false;
    return end.getTime() < today.getTime();
  }, [rangeEnd]);

  const applyPeriod = useCallback((periodKey, anchorDate = getMskToday()) => {
    const next = buildPeriodRange(periodKey, anchorDate);
    setPeriodFilter(periodKey);
    setRangeStart(next.rangeStart);
    setRangeEnd(next.rangeEnd);
  }, []);

  const handlePeriodChipClick = useCallback((periodKey) => {
    applyPeriod(periodKey, getMskToday());
  }, [applyPeriod]);

  const handlePrevRange = useCallback(() => {
    if (periodFilter === 'week') {
      applyPeriod('week', addMskCalendarDays(rangeStart, -7));
      return;
    }
    if (periodFilter === 'month') {
      const start = normalizeMskCalendarDate(rangeStart) || getMskToday();
      const year = start.getFullYear();
      const month = start.getMonth() + 1;
      const prevYear = month === 1 ? year - 1 : year;
      const prevMonth = month === 1 ? 12 : month - 1;
      applyPeriod('month', createMskCalendarDate(prevYear, prevMonth, 1));
      return;
    }
    const span = getMskRangeSpanDays(rangeStart, rangeEnd);
    const nextEnd = addMskCalendarDays(rangeEnd, -span);
    const nextStart = addMskCalendarDays(rangeStart, -span);
    if (!nextEnd || !nextStart) return;
    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
    if (periodFilter !== '24h') {
      setPeriodFilter('custom');
    }
  }, [applyPeriod, periodFilter, rangeEnd, rangeStart]);

  const handleNextRange = useCallback(() => {
    if (!canGoNext) return;
    if (periodFilter === 'week') {
      applyPeriod('week', addMskCalendarDays(rangeStart, 7));
      return;
    }
    if (periodFilter === 'month') {
      const start = normalizeMskCalendarDate(rangeStart) || getMskToday();
      const year = start.getFullYear();
      const month = start.getMonth() + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const nextMonth = month === 12 ? 1 : month + 1;
      applyPeriod('month', createMskCalendarDate(nextYear, nextMonth, 1));
      return;
    }
    const span = getMskRangeSpanDays(rangeStart, rangeEnd);
    const today = getMskToday();
    let nextEnd = addMskCalendarDays(rangeEnd, span);
    if (!nextEnd) return;
    if (nextEnd.getTime() > today.getTime()) {
      nextEnd = today;
    }
    const nextStart = addMskCalendarDays(nextEnd, -(span - 1));
    if (!nextStart) return;
    setRangeStart(nextStart);
    setRangeEnd(nextEnd);
  }, [applyPeriod, canGoNext, periodFilter, rangeEnd, rangeStart]);

  const handleCustomRangeChange = useCallback((startDate, endDate) => {
    const normalizedStart = normalizeMskCalendarDate(startDate);
    const normalizedEnd = normalizeMskCalendarDate(endDate);
    if (!normalizedStart || !normalizedEnd) return;
    const [from, to] = normalizedStart <= normalizedEnd
      ? [normalizedStart, normalizedEnd]
      : [normalizedEnd, normalizedStart];
    setRangeStart(from);
    setRangeEnd(to);
    setPeriodFilter('custom');
  }, []);

  const handleDateRangePeriodReset = useCallback(() => {
    const key = periodFilter === 'custom' ? '24h' : periodFilter;
    applyPeriod(key, getMskToday());
  }, [applyPeriod, periodFilter]);

  const seriesBounds = useMemo(
    () => toSeriesBounds(periodFilter, rangeStart, rangeEnd),
    [periodFilter, rangeStart, rangeEnd],
  );

  const loadSeries = useCallback(async () => {
    setSeriesLoading(true);
    try {
      const response = await apiService.getLocationCheckSeries(target.id, {
        view: aggregation,
        metric,
        from_ts: seriesBounds.from_ts,
        to_ts: seriesBounds.to_ts,
      });
      setSeriesPayload(response.data?.data || null);
    } catch (error) {
      message.error(error.response?.data?.detail || error.message || 'Ошибка серии');
    } finally {
      setSeriesLoading(false);
    }
  }, [target.id, aggregation, metric, seriesBounds]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const busy = actionId === target.id;
  const periodLabel = periodFilter === 'custom'
    ? dateRangeLabel
    : (RANGE_OPTIONS.find((item) => item.key === periodFilter)?.label || dateRangeLabel);

  const rfSeries = useMemo(() => {
    const payload = seriesPayload?.rf?.[rfMetric];
    return Array.isArray(payload) ? payload : [];
  }, [seriesPayload, rfMetric]);

  const hasRfData = useMemo(
    () => rfSeries.some((item) => (
      Array.isArray(item.values)
        ? item.values.some((value) => value != null)
        : (item.points || []).some((point) => point.v != null)
    )),
    [rfSeries],
  );

  const speedMetricLabel = SPEED_METRIC_OPTIONS.find((item) => item.key === metric)?.label || metric;

  const tooltipGroups = useMemo(() => ([
    {
      id: 'speed',
      title: speedMetricLabel,
      metric,
      qualityMetric: metric,
      series: seriesPayload?.series || [],
    },
    {
      id: 'rsrp',
      title: 'RSRP',
      metric: 'rsrp',
      qualityMetric: 'rsrp',
      series: Array.isArray(seriesPayload?.rf?.rsrp) ? seriesPayload.rf.rsrp : [],
    },
    {
      id: 'snr',
      title: 'SNR',
      metric: 'snr',
      qualityMetric: 'snr',
      series: Array.isArray(seriesPayload?.rf?.snr) ? seriesPayload.rf.snr : [],
    },
  ]), [speedMetricLabel, metric, seriesPayload?.series, seriesPayload?.rf]);

  const handleHoverBucketChange = useCallback((nextHover) => {
    if (hoverLeaveTimerRef.current) {
      window.clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
    if (nextHover == null) {
      hoverLeaveTimerRef.current = window.setTimeout(() => {
        setHoverState(null);
        hoverLeaveTimerRef.current = null;
      }, 60);
      return;
    }
    setHoverState(nextHover);
  }, []);

  useEffect(() => () => {
    if (hoverLeaveTimerRef.current) {
      window.clearTimeout(hoverLeaveTimerRef.current);
    }
  }, []);

  return (
    <article
      className="lc-target-card"
      aria-label={`Подключение ${target.connection_name || '—'}, модем ${target.modem_id}, оператор ${target.operator || '—'}`}
    >
      <header className="lc-target-card__header">
        <div className="lc-target-card__meta">
          <h2 className="lc-target-card__title">
            <span className="lc-target-card__title-part">
              <span className="lc-target-card__title-label">Подключение</span>
              {' '}
              {target.connection_name || '—'}
            </span>
            <span className="lc-target-card__title-sep" aria-hidden="true">·</span>
            <span className="lc-target-card__title-part">
              <span className="lc-target-card__title-label">Модем</span>
              {' '}
              #{target.modem_id}
              {target.modem_name ? ` ${target.modem_name}` : ''}
            </span>
            <span className="lc-target-card__title-sep" aria-hidden="true">·</span>
            <span className="lc-target-card__title-part">
              <span className="lc-target-card__title-label">Оператор</span>
              {' '}
              {target.operator || '—'}
            </span>
          </h2>
          <p className="lc-target-card__sub">
            Прокси #{target.proxy_id}
            {target.proxy_login ? ` · ${target.proxy_login}` : ''}
          </p>
        </div>
        <span className={statusBadgeClass(target.enabled ? target.status : 'paused')}>
          {statusBadgeLabel(target.enabled ? target.status : 'paused')}
        </span>
      </header>

      <div className="lc-target-card__toolbar" role="toolbar" aria-label="Вид графика">
        <div className="lc-segment" role="group" aria-label="Тип графика">
          {CHART_TYPE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`lc-segment__btn${chartType === option.key ? ' lc-segment__btn--active' : ''}`}
              onClick={() => setChartType(option.key)}
              aria-pressed={chartType === option.key}
              tabIndex={0}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="lc-segment" role="group" aria-label="Агрегация">
          {AGGREGATION_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`lc-segment__btn${aggregation === option.key ? ' lc-segment__btn--active' : ''}`}
              onClick={() => setAggregation(option.key)}
              aria-pressed={aggregation === option.key}
              tabIndex={0}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="lc-target-card__period-controls">
          <div className="lc-segment" role="group" aria-label="Период">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`lc-segment__btn${periodFilter === option.key ? ' lc-segment__btn--active' : ''}`}
                onClick={() => handlePeriodChipClick(option.key)}
                aria-pressed={periodFilter === option.key}
                tabIndex={0}
              >
                {option.label}
              </button>
            ))}
          </div>
          <DashboardSalesDateRange
            className="dashboard-header-row__date-range--in-toolbar lc-target-card__date-range"
            dateRangeLabel={dateRangeLabel}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            periodFilter={periodFilter === '24h' ? 'day' : periodFilter}
            canGoNext={canGoNext}
            onPrevRange={handlePrevRange}
            onNextRange={handleNextRange}
            onCustomRangeChange={handleCustomRangeChange}
            onPeriodFilterChange={handleDateRangePeriodReset}
          />
        </div>
        <div className="lc-segment" role="group" aria-label="Метрика скорости">
          {SPEED_METRIC_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`lc-segment__btn${metric === option.key ? ' lc-segment__btn--active' : ''}`}
              onClick={() => setMetric(option.key)}
              aria-pressed={metric === option.key}
              tabIndex={0}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="lc-target-card__charts">
        <div className="lc-target-card__chart">
          <h3 className="lc-target-card__chart-title">Скорость</h3>
          {seriesLoading && !seriesPayload ? (
            <p className="lc-chart__empty">Загрузка графика…</p>
          ) : (
            <LocationCheckChart
              chartId="speed"
              chartType={chartType}
              aggregation={aggregation}
              metric={metric}
              labels={seriesPayload?.labels || []}
              series={seriesPayload?.series || []}
              fromTs={seriesPayload?.from || seriesBounds.from_ts}
              toTs={seriesPayload?.to || seriesBounds.to_ts}
              tooltipDayLabel={tooltipDayLabel}
              ariaLabel={`Скорость модема ${target.modem_id}`}
              hoverBucketIndex={hoverState?.bucketIndex ?? null}
              hoverSeriesId={hoverState?.seriesId ?? null}
              hoverPointer={hoverState?.source === 'speed' ? hoverState : null}
              onHoverBucketChange={handleHoverBucketChange}
              showTooltip={hoverState?.source === 'speed'}
              tooltipGroups={tooltipGroups}
            />
          )}
          {seriesPayload?.stats ? (
            <p className="lc-target-card__stats">
              Период: {periodLabel}
              {' · '}
              {dateRangeLabel}
              {' · '}
              Сэмплов: {seriesPayload.stats.samples}, успешно: {seriesPayload.stats.ok}, недоступно:{' '}
              {seriesPayload.stats.unavailable}
            </p>
          ) : null}
        </div>

        <div className="lc-target-card__rf" aria-label="RF график">
          <div className="lc-target-card__rf-toolbar">
            <h3 className="lc-target-card__rf-title">Качество сигнала</h3>
            <div className="lc-segment" role="group" aria-label="RF метрика">
              {RF_METRIC_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`lc-segment__btn${rfMetric === option.key ? ' lc-segment__btn--active' : ''}`}
                  onClick={() => setRfMetric(option.key)}
                  aria-pressed={rfMetric === option.key}
                  tabIndex={0}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {seriesLoading && !seriesPayload ? (
            <p className="lc-chart__empty">Загрузка RF…</p>
          ) : hasRfData ? (
            <LocationCheckChart
              chartId="rf"
              chartType={chartType}
              aggregation={aggregation}
              metric={rfMetric}
              labels={seriesPayload?.labels || []}
              series={rfSeries}
              fromTs={seriesPayload?.from || seriesBounds.from_ts}
              toTs={seriesPayload?.to || seriesBounds.to_ts}
              tooltipDayLabel={tooltipDayLabel}
              ariaLabel={`${rfMetric.toUpperCase()} модема ${target.modem_id}`}
              hoverBucketIndex={hoverState?.bucketIndex ?? null}
              hoverSeriesId={hoverState?.seriesId ?? null}
              hoverPointer={hoverState?.source === 'rf' ? hoverState : null}
              onHoverBucketChange={handleHoverBucketChange}
              showTooltip={hoverState?.source === 'rf'}
              tooltipGroups={tooltipGroups}
            />
          ) : (
            <p className="lc-chart__empty">Нет RF-данных за выбранный период</p>
          )}
        </div>
      </div>

      {target.last_error ? (
        <p className="lc-target-card__error">{target.last_error}</p>
      ) : null}

      <div className="lc-actions">
        {target.enabled ? (
          <button
            type="button"
            className="lc-actions__btn"
            disabled={busy}
            onClick={() => onPause(target)}
            tabIndex={0}
          >
            Пауза
          </button>
        ) : (
          <button
            type="button"
            className="lc-actions__btn"
            disabled={busy}
            onClick={() => onResume(target)}
            tabIndex={0}
          >
            Возобновить
          </button>
        )}
        <button
          type="button"
          className="lc-actions__btn"
          disabled={busy || !target.enabled}
          onClick={() => onRunNow(target)}
          tabIndex={0}
        >
          Сейчас
        </button>
        <button
          type="button"
          className="lc-actions__btn"
          disabled={seriesLoading}
          onClick={loadSeries}
          tabIndex={0}
        >
          Обновить график
        </button>
        <button
          type="button"
          className="lc-actions__btn lc-actions__btn--danger"
          disabled={busy}
          onClick={() => onRemove(target)}
          tabIndex={0}
        >
          Убрать
        </button>
      </div>
    </article>
  );
};

const NO_CONNECTION_FILTER_VALUE = 'none';

const resolveTargetConnectionFilterValue = (target) => {
  if (target?.connection_id == null) {
    return NO_CONNECTION_FILTER_VALUE;
  }
  return String(target.connection_id);
};

const resolveTargetConnectionFilterLabel = (target) => {
  if (target?.connection_name) {
    return target.connection_name;
  }
  if (target?.connection_id != null) {
    return `Подключение #${target.connection_id}`;
  }
  return 'Без подключения';
};

const buildServerFilterOptions = (targetsList) => {
  const optionsByValue = new Map();

  targetsList.forEach((target) => {
    const value = resolveTargetConnectionFilterValue(target);
    if (optionsByValue.has(value)) {
      return;
    }
    optionsByValue.set(value, {
      value,
      label: resolveTargetConnectionFilterLabel(target),
    });
  });

  return Array.from(optionsByValue.values()).sort((left, right) => (
    left.label.localeCompare(right.label, 'ru')
  ));
};

const LocationCheck = () => {
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState([]);
  const [selectedServerIds, setSelectedServerIds] = useState([]);
  const [tariffInfo, setTariffInfo] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Затемнение окна настроек закрывает только настоящим щелчком (владелец 29.10, DF85)
  const settingsBackdrop = useBackdropClose(() => setSettingsOpen(false));
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [form] = Form.useForm();

  const serverFilterOptions = useMemo(
    () => buildServerFilterOptions(targets),
    [targets]
  );

  const filteredTargets = useMemo(() => {
    if (!selectedServerIds.length) {
      return targets;
    }

    const selected = new Set(selectedServerIds);
    return targets.filter((target) => (
      selected.has(resolveTargetConnectionFilterValue(target))
    ));
  }, [targets, selectedServerIds]);

  useEffect(() => {
    setSelectedServerIds((prev) => {
      if (!prev.length) {
        return prev;
      }

      const available = new Set(serverFilterOptions.map((option) => option.value));
      const next = prev.filter((value) => available.has(value));
      return next.length === prev.length ? prev : next;
    });
  }, [serverFilterOptions]);

  const handleServerFilterChange = (values) => {
    setSelectedServerIds(Array.isArray(values) ? values : []);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [targetsRes, settingsRes, tariffRes] = await Promise.all([
        apiService.getLocationCheckTargets(),
        apiService.getLocationCheckSettings(),
        apiService.getLocationCheckTariff(),
      ]);
      setTargets(targetsRes.data?.data || []);
      setTariffInfo(tariffRes.data || null);
      const settings = settingsRes.data?.data || {};
      form.setFieldsValue({
        bands: settings.bands || ['B1', 'B3', 'B7'],
        interval_min: settings.interval_min ?? 60,
        retry_on_fail_min: settings.retry_on_fail_min ?? 1,
        retry_attempts: settings.retry_attempts ?? 3,
        band_settle_sec: settings.band_settle_sec ?? 20,
        speedtest_timeout_sec: settings.speedtest_timeout_sec ?? 45,
        enabled: settings.enabled !== false,
      });
    } catch (error) {
      message.error(error.response?.data?.detail || error.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleSaveSettings = async () => {
    try {
      const values = await form.validateFields();
      setSettingsSaving(true);
      await apiService.updateLocationCheckSettings(values);
      message.success('Настройки сохранены');
      setSettingsOpen(false);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.detail || error.message || 'Не удалось сохранить');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handlePause = async (target) => {
    setActionId(target.id);
    try {
      await apiService.pauseLocationCheckTarget(target.id);
      message.success('Пауза');
      await loadData();
    } catch (error) {
      message.error(error.response?.data?.detail || error.message);
    } finally {
      setActionId(null);
    }
  };

  const handleResume = async (target) => {
    setActionId(target.id);
    try {
      await apiService.resumeLocationCheckTarget(target.id);
      message.success('Возобновлено');
      await loadData();
    } catch (error) {
      message.error(error.response?.data?.detail || error.message);
    } finally {
      setActionId(null);
    }
  };

  const handleRunNow = async (target) => {
    setActionId(target.id);
    try {
      await apiService.runLocationCheckTargetNow(target.id);
      message.success('Проверка поставлена в очередь');
      await loadData();
    } catch (error) {
      message.error(error.response?.data?.detail || error.message);
    } finally {
      setActionId(null);
    }
  };

  const handleRemove = (target) => {
    Modal.confirm({
      title: 'Убрать из проверки локаций?',
      content: `Модем #${target.modem_id}, прокси #${target.proxy_id}`,
      okText: 'Убрать',
      cancelText: 'Отмена',
      okType: 'danger',
      onOk: async () => {
        setActionId(target.id);
        try {
          await apiService.deleteLocationCheckTarget(target.id);
          message.success('Удалено');
          await loadData();
        } catch (error) {
          message.error(error.response?.data?.detail || error.message);
        } finally {
          setActionId(null);
        }
      },
    });
  };

  return (
    <div className="lc-page">
      <header className="lc-header">
        <h1 className="lc-header__title">Проверка локаций</h1>
        <div className="lc-header__filters">
          <Select
            className="lc-header__server-select"
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            placeholder="Все серверы"
            aria-label="Фильтр по серверам"
            value={selectedServerIds}
            onChange={handleServerFilterChange}
            options={serverFilterOptions}
            disabled={loading || serverFilterOptions.length === 0}
            getPopupContainer={(node) => node.parentElement || document.body}
          />
        </div>
        <button
          type="button"
          className="lc-header__settings"
          onClick={handleOpenSettings}
          aria-label="Настройки проверки локаций"
          tabIndex={0}
        >
          <SettingsGearIcon />
        </button>
      </header>

      {loading ? (
        <section className="lc-card">
          <p className="lc-empty__text">Загрузка…</p>
        </section>
      ) : targets.length === 0 ? (
        <section className="lc-card">
          <div className="lc-empty">
            <h2 className="lc-empty__title">Нет целей мониторинга</h2>
            <p className="lc-empty__text">
              Добавьте активный прокси тарифа «{LOCATION_CHECK_TARIFF_NAME}» через список прокси
              (меню строки / панель инфо / массовое действие).
              {tariffInfo && tariffInfo.exists === false
                ? ' Тариф ещё не создан — выполните миграцию apply_location_check_migration.py.'
                : null}
            </p>
          </div>
        </section>
      ) : filteredTargets.length === 0 ? (
        <section className="lc-card">
          <div className="lc-empty">
            <h2 className="lc-empty__title">Нет целей по выбранным серверам</h2>
            <p className="lc-empty__text">
              Снимите фильтр или выберите другие серверы в шапке.
            </p>
          </div>
        </section>
      ) : (
        <div className="lc-targets">
          {filteredTargets.map((target) => (
            <TargetCard
              key={target.id}
              target={target}
              actionId={actionId}
              onPause={handlePause}
              onResume={handleResume}
              onRunNow={handleRunNow}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}

      <Modal
        className="light-modal lc-settings-modal"
        title="Настройки проверки локаций"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        maskClosable={false}
        wrapProps={settingsBackdrop}
        onOk={handleSaveSettings}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={settingsSaving}
        destroyOnClose={false}
        centered
      >
        <Form form={form} layout="vertical" className="lc-settings-form">
          <Form.Item name="enabled" label="Планировщик включён" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="bands"
            label="Bands для ротации"
            rules={[{ required: true, message: 'Выберите хотя бы один band' }]}
          >
            <Select mode="multiple" options={BAND_OPTIONS} placeholder="B1, B3…" />
          </Form.Item>
          <Form.Item name="interval_min" label="Интервал, мин" rules={[{ required: true }]}>
            <InputNumber min={1} max={24 * 60} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="retry_on_fail_min" label="Повтор при ошибке, мин" rules={[{ required: true }]}>
            <InputNumber min={1} max={24 * 60} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="retry_attempts" label="Попыток на band" rules={[{ required: true }]}>
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="band_settle_sec" label="Пауза после смены band, сек" rules={[{ required: true }]}>
            <InputNumber min={0} max={300} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="speedtest_timeout_sec" label="Таймаут speedtest, сек" rules={[{ required: true }]}>
            <InputNumber min={5} max={300} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LocationCheck;
